import {
  getMsalInstance,
  getMsalInstanceAsync,
  loginRequest,
  apiScope,
  fetchAuthConfig,
  stampMsalLoginStarted,
  clearMsalLoginStamp,
  clearMsalLoginStampIfStale,
  AUTH_CONFIG_FETCH_TIMEOUT_MS
} from "./MsalConfig";
import { fetchWithStartupRetry } from "../utils/retryOnStartup";

interface Token {
  token: string;
  expires: Date;
}

// Hard ceiling on a silent Entra refresh. msal.acquireTokenSilent / ssoSilent and
// the token-exchange fetch can hang and NEVER settle on a stale Entra session.
// Both PrivateRoute (renders <Warmup> while awaiting) and useWarmup await this —
// if it never resolves, the loading splash hangs forever with no way out (incident
// 2026-06-19, admin-staging stuck on splash). Bounding it guarantees callers always
// proceed: a failed/timed-out refresh resolves false → PrivateRoute redirects to a
// clean login automatically. No manual cache-clearing required.
export const ENTRA_REFRESH_TIMEOUT_MS = 8000;

// Re-exported from MsalConfig (its single definition, next to the fetch it
// bounds) so PrivateRoute can derive its wall-clock ceiling from one import.
export { AUTH_CONFIG_FETCH_TIMEOUT_MS };

class AuthService {
  constructor() {
    this.loadTokenFromStorage();
    this.loadLegacyTokenFromStorage();
    this.loadProfileFromStorage();
  }

  token: Token = {
    token: "",
    expires: new Date(Date.now() - 1000)
  };

  legacyToken: Token = {
    token: "",
    expires: new Date(Date.now() - 1000)
  };

  profile: {
    id: string;
    siteId: number;
    userName: string;
    picture: string;
    firstName: string;
    lastName: string;
    userId: number;
    timeZone?: string | null;
    isVelaroSupport?: boolean;
    isVelaroAdmin?: boolean;
    isVelaroBilling?: boolean;
  } = {
    id: "",
    siteId: 0,
    userName: "",
    picture: "",
    firstName: "",
    lastName: "",
    userId: 0,
    timeZone: null,
    isVelaroSupport: false,
    isVelaroAdmin: false
  };

  updateProfileTimezone(tz: string | null) {
    this.profile = { ...this.profile, timeZone: tz };
    localStorage.setItem("profile", JSON.stringify(this.profile));
  }

  /**
   * Keeps the token-exchange profile cache (used to seed ProfileProvider before
   * Profile/Me returns — see ProfileProvider.tsx seedProfile) in sync after the
   * user changes their avatar via AvatarPickerModal, so a reload doesn't
   * flash the old picture before the fresh fetch completes.
   */
  updateProfilePicture(pictureUrl: string) {
    this.profile = { ...this.profile, picture: pictureUrl };
    localStorage.setItem("profile", JSON.stringify(this.profile));
  }

  loadProfileFromStorage() {
    const profileJson = localStorage.getItem("profile");

    if (profileJson) {
      this.profile = JSON.parse(profileJson);
    }
  }

  loadTokenFromStorage() {
    const item = localStorage.getItem("token");

    if (!item) {
      return;
    }

    const data = JSON.parse(item);

    this.token = {
      token: data.token,
      expires: new Date(data.expires)
    };
  }

  loadLegacyTokenFromStorage() {
    const item = localStorage.getItem("legacyToken");

    if (!item) {
      return;
    }

    const data = JSON.parse(item);

    this.legacyToken = {
      token: data.token,
      expires: new Date(data.expires)
    };
  }

  isAuthenticated() {
    if (!this.token) {
      return false;
    }

    return this.token.expires.getTime() > Date.now() && this.token.token !== "";
  }

  isEntraSession(): boolean {
    return localStorage.getItem("authMode") === "entra";
  }

  // "Try a Different Account" — used by EntraCallback.tsx's isAuthRejection branch
  // (formerly also by the now-removed V10-bridge LoginUnauthorized page). Previously
  // this just replaced the URL back into the app, which re-triggered PrivateRoute's
  // silent SSO with the SAME cached account and looped straight back. Now it forces
  // Microsoft's real account picker instead.
  retryLogin() {
    this.loginRedirect({ forceAccountPicker: true });
  }

  /**
   * Admin login is Entra-only — the legacy V10 bridge login (temp-token exchange
   * via VITE_AUTH_URL/identity/authenticate → /login/callback → POST /Token) is
   * permanently retired. It used to be reached as a silent fallback whenever the
   * runtime /auth/config fetch below failed for ANY reason (a network blip, a slow
   * response) — but prod has never had AuthServerUrl/AuthServerSecret app settings
   * configured, so that fallback was a guaranteed dead end showing a generic
   * "Login Error" screen with no working recovery (2026-08-03 incident — Alex
   * Bloom, whose account was and is fully healthy, hit this on a transient blip).
   * Login always goes through Entra now; a user with no WorkspaceUser/IdentityMapping
   * record gets EntraTokenExchangeAsync's "user_not_found" message telling them to
   * get an account provisioned in messaging first, rather than a broken fallback.
   */
  async loginRedirect(options?: { forceAccountPicker?: boolean }) {
    // pathname + search, NOT pathname alone. Every OAuth integration's connect
    // flow returns the browser to a URL whose QUERY STRING carries the entire
    // result (e.g. /integrations/outlook-inbox?outlookConnected=true, or
    // ?outlookError=invalid_nonce). Stamping only the pathname silently threw
    // that away, so any customer who passed through a login round-trip on the
    // way back from a provider landed on the integration page with no success
    // or failure message at all. Incident 2026-08-28.
    sessionStorage.setItem(
      "redirectUri",
      `${window.location.pathname}${window.location.search}`
    );
    // Pre-load Entra config for MSAL instance creation (authority/client id can be
    // runtime-configured via /auth/config; entraLoginRedirect also awaits this).
    await fetchAuthConfig();
    this.entraLoginRedirect(options);
  }

  // Removes every msal.* key from both storages MSAL is configured to use
  // (cacheLocation: "localStorage", plus sessionStorage for in-flight PKCE state).
  // Used when the caller explicitly wants a clean slate, e.g. forceAccountPicker
  // below, or an admin switching to a freshly-minted account on a shared machine.
  private clearMsalAccountCache() {
    const purge = (storage: Storage) => {
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (k && k.startsWith("msal.")) keys.push(k);
      }
      keys.forEach((k) => storage.removeItem(k));
    };
    try {
      purge(localStorage);
      purge(sessionStorage);
    } catch (e) {
      console.warn("[Auth] clearMsalAccountCache failed", e);
    }
  }

  async entraLoginRedirect(options?: { forceAccountPicker?: boolean }) {
    const msal = await getMsalInstanceAsync();
    if (!msal) {
      console.error("[Auth] MSAL not configured");
      return;
    }
    try {
      await msal.initialize();

      // Handle redirect promise first, clears any stale interaction
      await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false });

      if (options?.forceAccountPicker) {
        // Explicit "this cached account is wrong" path: wipe the cache so ssoSilent
        // has nothing to hint at, and pass prompt: "select_account" so Microsoft
        // shows its real account chooser instead of silently reusing an SSO
        // session at the CIAM/login.velaro.com level. Never falls through to the
        // silent branch below, that is the exact loop this exists to break.
        this.clearMsalAccountCache();
        stampMsalLoginStarted();
        await msal.loginRedirect({ ...loginRequest, prompt: "select_account" });
        return;
      }

      // Try silent SSO first, if the CIAM session is still alive, this succeeds
      // via a hidden iframe with no visible navigation. Avoids shipping users to
      // a Microsoft page that could go stale.
      //
      // Fixed 2026-08-21 (QA report, site 100050): loginHint used to be
      // accounts[0]?.username unconditionally — with 2+ cached MSAL accounts (e.g. a
      // staging test identity cached from earlier testing, still sitting in this
      // browser's MSAL cache alongside the user's real production account),
      // getAllAccounts()'s order is not "most recently used" and is not guaranteed
      // stable, so this could hint ssoSilent toward the WRONG cached identity. If
      // that identity's CIAM session was still alive, ssoSilent succeeded silently
      // (no visible prompt) as the wrong account — reported live as "attempting to
      // log into production but silently authenticated against a staging account
      // instead." Only supply a hint when exactly one cached account exists (truly
      // unambiguous); with 0 or 2+ accounts, pass none and let ssoSilent/the
      // loginRedirect fallback below surface Microsoft's real account chooser
      // instead of guessing.
      const accounts = msal.getAllAccounts();
      try {
        const ssoResponse = await msal.ssoSilent({
          ...loginRequest,
          ...(accounts.length === 1 ? { loginHint: accounts[0].username } : {})
        });
        if (ssoResponse?.accessToken) {
          console.log("[Auth] SSO silent succeeded, no redirect needed");
          await this.exchangeEntraToken(ssoResponse.accessToken);
          window.location.replace(sessionStorage.getItem("redirectUri") || "/");
          return;
        }
      } catch (ssoErr: any) {
        // A backend 403/401 here means ssoSilent succeeded (the CIAM session is
        // alive) but for an identity our backend rejects — e.g. a stale cached
        // account under login.velaro.com different from the one with access. This
        // is NOT an MSAL/network failure, so falling through to a plain
        // msal.loginRedirect(loginRequest) below would just silently re-authenticate
        // as the SAME wrong account again (same CIAM session cookie, no prompt).
        // Force the real account picker instead, same fix as retryLogin()/the
        // background refresh loop below.
        if (ssoErr?.isAuthRejection) {
          console.warn(
            "[Auth] Backend rejected the SSO-silent identity — forcing account picker"
          );
          this.clearMsalAccountCache();
          stampMsalLoginStarted();
          await msal.loginRedirect({
            ...loginRequest,
            prompt: "select_account"
          });
          return;
        }
        console.warn(
          "[Auth] SSO silent failed, using loginRedirect:",
          ssoErr?.errorCode
        );
      }

      stampMsalLoginStarted();
      await msal.loginRedirect(loginRequest);
    } catch (err: any) {
      if (err?.isAuthRejection) {
        // Same identity-rejection case reached via a different path (e.g.
        // exchangeEntraToken threw after msal.loginRedirect's own retry above).
        // Never fall through to the generic swallow-and-log branch below —
        // that left the user stranded with no recovery, only a console error.
        console.warn(
          "[Auth] Backend rejected the identity during login — forcing account picker"
        );
        this.clearMsalAccountCache();
        try {
          stampMsalLoginStarted();
          await msal.loginRedirect({
            ...loginRequest,
            prompt: "select_account"
          });
        } catch (retryErr) {
          console.error(
            "[Auth] MSAL loginRedirect (forced account picker) failed",
            retryErr
          );
        }
      } else if (err?.errorCode === "interaction_in_progress") {
        // Bug found 2026-08-27 (Brian Bloom, Multipoint, first-ever login): this used
        // to clear `msal.${clientId}.interaction.status`, which is NOT a real MSAL
        // cache key — MSAL's own PREFIX constant is the bare string "msal" (not
        // clientId-scoped), so the actual key is always `msal.interaction.status`
        // (verified against @azure/msal-browser's BrowserCacheManager). The wrong key
        // meant removeItem() was always a no-op, the retry below hit the exact same
        // interaction_in_progress error again, and the user was stuck on a silent
        // spinner forever with only a console.error — the only working fix was
        // manually clearing all browser storage. Fixed key + last-resort full purge
        // + reload, so a new customer's first login self-recovers instead of hanging.
        console.warn("[Auth] Clearing stale interaction_in_progress flag");
        localStorage.removeItem("msal.interaction.status");
        try {
          stampMsalLoginStarted();
          await msal.loginRedirect(loginRequest);
        } catch (retryErr) {
          console.error(
            "[Auth] MSAL loginRedirect retry failed, purging full MSAL cache and reloading",
            retryErr
          );
          this.clearMsalAccountCache();
          window.location.replace("/");
        }
      } else {
        console.error("[Auth] MSAL loginRedirect failed", err);
      }
    }
  }

  // Admin login/logout is Entra-only (see loginRedirect() above) — the V10 fallback
  // that used to live here pointed at the same permanently-retired, unconfigured
  // legacy auth server as the login path.
  logout() {
    this.entraLogout();
  }

  async entraLogout() {
    const msal = await getMsalInstanceAsync();
    localStorage.removeItem("token");
    localStorage.removeItem("legacyToken");
    localStorage.removeItem("profile");
    localStorage.removeItem("authMode");

    if (msal) {
      try {
        await msal.initialize();
        const accounts = msal.getAllAccounts();
        if (accounts.length === 0) {
          // No cached MSAL session — skip the logout endpoint (it would show a blank
          // account picker) and just redirect back to the app root.
          window.location.replace("/");
          return;
        }
        const account = accounts[0];
        await msal.logoutRedirect({
          account,
          // logoutHint skips the "pick an account" screen on the CIAM side
          logoutHint: account.username,
          postLogoutRedirectUri: window.location.origin
        });
      } catch (err) {
        console.error("[Auth] MSAL logout failed", err);
        window.location.replace("/");
      }
    } else {
      window.location.replace("/");
    }
  }

  logoutCallback() {
    localStorage.removeItem("token");
    localStorage.removeItem("legacyToken");
    localStorage.removeItem("authMode");
    window.location.replace("/");
  }

  // Local-only logout: clears app + MSAL state without bouncing through CIAM's
  // /oauth2/v2.0/logout endpoint. Used when silent token refresh fails — if the
  // CIAM session is already gone, a logoutRedirect can strand the user on
  // login.velaro.com (AADSTS900561) instead of returning them to the app.
  localOnlyLogout() {
    try {
      // Do NOT clear sessionStorage — MSAL stores the PKCE code verifier there
      // during a loginRedirect. Wiping it mid-redirect breaks the auth code exchange.
      localStorage.removeItem("token");
      localStorage.removeItem("legacyToken");
      localStorage.removeItem("profile");
      localStorage.removeItem("authMode");
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (
          k &&
          (k.startsWith("msal.") ||
            k.includes(".msal.") ||
            k === "velaro.msalLoginStartedAt")
        ) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore */
    }
    window.location.replace("/");
  }

  // V10 legacy bridge login (temp-token exchange: refresh()/fetchAccessToken()/
  // handleLoginCallback(), the /login/callback → POST /Token flow) removed 2026-08-11.
  // Admin login is Entra-only -- see loginRedirect() above. These methods were only
  // reachable via the /login/callback route, which nothing in-app ever navigated to;
  // prod has never had the AuthServerUrl/AuthServerSecret backend config this needed.

  // --- Entra token exchange ---

  async exchangeEntraToken(entraAccessToken: string): Promise<boolean> {
    const apiUrl = `${import.meta.env.VITE_ADMIN_API_URL}/auth/entra/token`;
    console.log("[Auth] Exchanging Entra token with admin API...");

    let response: Response;
    try {
      // Retried once — a cold-starting/mid-deploy backend can make this single
      // request time out even though ssoSilent already succeeded; without a
      // retry the user is stranded on the splash/callback page with a scary
      // "unreachable" error for what's really a few-second restart window.
      response = await fetchWithStartupRetry(() =>
        fetch(apiUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${entraAccessToken}`,
            "Content-Type": "application/json"
          }
        })
      );
    } catch (fetchErr: any) {
      console.error("[Auth] fetch() threw:", fetchErr?.message);
      throw new Error(
        "Unable to reach the authentication server. Please check your network connection and try again."
      );
    }

    console.log("[Auth] Token exchange response:", response.status);

    if (!response.ok) {
      let serverMessage: string | null = null;
      try {
        const errorJson = await response.json();
        serverMessage = errorJson?.message ?? null;
        console.error(
          "[Auth] Entra token exchange failed",
          response.status,
          errorJson?.error,
          errorJson?.message,
          errorJson?.correlationId
        );
      } catch {
        console.error("[Auth] Entra token exchange failed", response.status);
      }

      // 403/401 mean OUR backend explicitly rejected the identity in the token it was
      // handed — this is never transient. Retrying with the exact same silently-cached
      // MSAL account (ssoSilent/acquireTokenSilent) will fail identically forever; the
      // only way out is an interactive re-login with prompt: "select_account" so the
      // user can pick a DIFFERENT account. Tag the error so every caller (the silent
      // background refresh loop, the initial ssoSilent path, and the callback error
      // page) can tell "wrong identity, force account picker" apart from a genuine
      // network/iframe blip that's safe to just retry next cycle.
      const isAuthRejection =
        response.status === 403 || response.status === 401;

      // Show the actual server error message so the user knows the real reason
      if (serverMessage) {
        const err = new Error(serverMessage);
        (err as any).isAuthRejection = isAuthRejection;
        throw err;
      }
      if (response.status === 403) {
        const err = new Error(
          "Access denied. You must be an Administrator to access the admin panel."
        );
        (err as any).isAuthRejection = true;
        throw err;
      }
      if (response.status === 401) {
        const err = new Error(
          "Authentication expired or invalid. Please try logging in again."
        );
        (err as any).isAuthRejection = true;
        throw err;
      }
      throw new Error("Login failed. Please try again or contact support.");
    }

    const data = await response.json();

    this.token = {
      token: data.token.token,
      expires: new Date(data.token.expires)
    };

    this.profile = data.profile;
    // No legacy token for Entra sessions
    this.legacyToken = { token: "", expires: new Date(Date.now() - 1000) };

    localStorage.setItem("token", JSON.stringify(this.token));
    localStorage.setItem("profile", JSON.stringify(data.profile));
    localStorage.setItem("authMode", "entra");

    return true;
  }

  async handleEntraCallback(): Promise<boolean> {
    const msal = await getMsalInstanceAsync();
    if (!msal) {
      console.error("[Auth] MSAL instance not available");
      return false;
    }

    try {
      console.log("[Auth] Initializing MSAL for callback...");
      await msal.initialize();

      console.log("[Auth] Calling handleRedirectPromise...");
      const response = await msal.handleRedirectPromise({
        navigateToLoginRequestUrl: false
      });
      // Callback reached — clear the stale-login watchdog stamp either way
      clearMsalLoginStamp();

      if (!response) {
        const accounts = msal.getAllAccounts();
        console.warn(
          "[Auth] handleRedirectPromise returned null. Accounts:",
          accounts.length
        );
        if (accounts.length > 0) {
          console.log(
            "[Auth] Found cached account, trying silent token acquisition..."
          );
          try {
            const silentResponse = await msal.acquireTokenSilent({
              scopes: [apiScope],
              account: accounts[0]
            });
            console.log("[Auth] Silent token acquired, exchanging...");
            return await this.exchangeEntraToken(silentResponse.accessToken);
          } catch (silentErr: any) {
            // A cached MSAL account with no live CIAM session throws here
            // (interaction_required / login_required / AADSTS160021 "Application
            // requested a user session which does not exist", etc.) — this just
            // means the account object is stale, not a real failure. Mirror
            // _entraTokenRefreshImpl's handling below: send the user to interactive
            // login instead of letting this propagate to the outer catch, which
            // previously surfaced a dead-end "Login Failed — Start Fresh" screen for
            // what recovers with a single interactive redirect. Confirmed live
            // 2026-08-11 (Alex, admin.velaro.com production): handleRedirectPromise
            // returned null with Accounts: 3, then acquireTokenSilent threw
            // AADSTS160021 and the user was stranded needing a manual "Start Fresh"
            // click (a hard refresh also happened to clear it, since that's a fresh
            // page load that re-runs this same recovery path — but it shouldn't take
            // a user action at all).
            if (silentErr?.isAuthRejection) {
              // Backend explicitly rejected this identity — not a session issue,
              // let it propagate so the outer catch's isAuthRejection handling applies.
              throw silentErr;
            }
            const code: string = silentErr?.errorCode ?? "";
            console.warn(
              "[Auth] acquireTokenSilent failed for cached account, redirecting to interactive login:",
              code || silentErr?.message
            );
            this.entraLoginRedirect();
            return false;
          }
        }
        // No response and no cached accounts — user likely refreshed mid-redirect or
        // navigated directly to the callback URL. Return false so the caller can
        // redirect back to "/" and let the normal login flow restart cleanly.
        return false;
      }

      let accessToken = response.accessToken;
      if (!accessToken) {
        console.warn(
          "[Auth] Redirect response has no accessToken, trying acquireTokenSilent..."
        );
        const silentResponse = await msal.acquireTokenSilent({
          scopes: [apiScope],
          account: response.account!
        });
        accessToken = silentResponse.accessToken;
      }
      if (!accessToken) {
        throw new Error("No access token obtained from MSAL.");
      }
      console.log("[Auth] Got access token, exchanging with admin backend...");
      return await this.exchangeEntraToken(accessToken);
    } catch (err: any) {
      console.error(
        "[Auth] Entra callback failed:",
        err?.errorCode,
        err?.message
      );
      // Pass through all error messages from exchangeEntraToken — they're already user-friendly.
      // Only fall back to generic message for truly unknown errors (no message at all).
      const outErr = new Error(
        err?.message || "Login failed. Please try again or contact support."
      );
      // Preserve isAuthRejection so EntraCallback.tsx can show "Wrong Account Signed In"
      // (auth.retryLogin() / forced account picker) instead of the generic dead-end
      // "Login Failed — Start Fresh" screen. Previously this was dropped when wrapping
      // the error in `new Error(...)` above — any 403/401 backend identity rejection
      // reaching this catch (e.g. from the direct accessToken exchange on lines
      // 513-526) silently downgraded to the wrong recovery screen.
      if (err?.isAuthRejection) (outErr as any).isAuthRejection = true;
      throw outErr;
    }
  }

  // Kept as a plain boolean (not derived from _entraRefreshPromise) because
  // checkAccessToken() below reads it synchronously as a fast "is a refresh
  // already running" guard — that guard is one of the three login-loop
  // protections from the 2026-05-12 postmortem and must stay truthy the instant
  // entraTokenRefresh() is entered, before any await.
  private _entraRefreshInProgress = false;

  // The single in-flight refresh. Concurrent callers JOIN this promise instead of
  // being told the session is gone.
  private _entraRefreshPromise: Promise<boolean> | null = null;

  /**
   * Silently renew the app JWT from the Entra session.
   *
   * Serialization contract: only ever ONE underlying refresh runs at a time
   * (the guarantee the 2026-05-12 login-loop postmortem actually needed).
   * Concurrent callers await that same refresh and receive its REAL result.
   *
   * This previously returned `false` immediately to any concurrent caller. That
   * was a fail-closed lie: `false` means "session gone" to PrivateRoute, which
   * latches redirectFired and calls loginRedirect(). On a cold load
   * checkAccessToken() fires a refresh at module-import time, so useWarmup AND
   * PrivateRoute both hit that bogus `false` while a perfectly valid refresh was
   * still in flight — sending users to a needless login round-trip and, when the
   * redirect failed to navigate, to a permanent loading screen. Incident
   * 2026-08-28; see docs/architecture/auth-login-loop-postmortem.md.
   */
  async entraTokenRefresh(): Promise<boolean> {
    if (this._entraRefreshPromise) return this._entraRefreshPromise;

    this._entraRefreshInProgress = true;

    // Race the silent refresh against a hard timeout so it ALWAYS settles. A hung
    // acquireTokenSilent/ssoSilent/token-exchange would otherwise leave PrivateRoute
    // and useWarmup awaiting a promise that never resolves → splash hangs forever.
    // On timeout we resolve false; the caller (PrivateRoute) then routes to a clean
    // login. The abandoned silent attempt has its own MSAL iframe timeout and is GC'd.
    //
    // The cached promise is the ALREADY-normalized one: a rejection is mapped to
    // `false` here so it fans out as a clean `false` to every joined caller rather
    // than as N separate unhandled rejections (each of which would otherwise hit
    // main.tsx's unhandledrejection handler and fire the crash-email pipeline).
    let raceTimer: any;
    const refresh = Promise.race([
      this._entraTokenRefreshImpl(),
      new Promise<boolean>((resolve) => {
        raceTimer = setTimeout(() => resolve(false), ENTRA_REFRESH_TIMEOUT_MS);
      })
    ])
      .finally(() => clearTimeout(raceTimer))
      .catch((err: any) => {
        console.warn("[Auth] Entra silent refresh threw:", err?.message ?? err);
        return false;
      })
      .finally(() => {
        this._entraRefreshInProgress = false;
        this._entraRefreshPromise = null;
      });

    this._entraRefreshPromise = refresh;
    return refresh;
  }

  private async _entraTokenRefreshImpl(): Promise<boolean> {
    // Error codes that mean the CIAM session is genuinely gone — force a clean logout
    // so the user gets a fresh login prompt instead of hitting 401s indefinitely.
    const SESSION_EXPIRED_CODES = new Set([
      "login_required",
      "interaction_required",
      "invalid_grant",
      "consent_required",
      "user_login_error"
    ]);

    try {
      // Inside the try: getMsalInstanceAsync() can reject (it fetches remote auth
      // config). Outside, that rejection escaped entraTokenRefresh()'s
      // try/finally (which had no catch) all the way to PrivateRoute's
      // un-caught .then(), leaving it stuck on the loading screen forever.
      const msal = await getMsalInstanceAsync();
      if (!msal) return false;

      await msal.initialize();
      const accounts = msal.getAllAccounts();
      if (accounts.length === 0) {
        // No cached MSAL account — do NOT call localOnlyLogout() here.
        // localOnlyLogout() purges all MSAL localStorage keys and navigates to "/",
        // which reloads the page with 0 MSAL accounts → this branch fires again → infinite loop.
        // Instead, try ssoSilent via a hidden iframe to recover the session silently.
        throw new Error("no_accounts");
      }

      const response = await msal.acquireTokenSilent({
        scopes: [apiScope],
        account: accounts[0]
      });

      return await this.exchangeEntraToken(response.accessToken);
    } catch (err: any) {
      const code: string = err?.errorCode ?? "";
      const msg: string = err?.message ?? "";

      // Backend explicitly rejected the silently-cached account's identity (403/401
      // from exchangeEntraToken) — this is NOT transient. The generic branch below
      // used to log this as "transient error (will retry)" and just return false,
      // which meant checkAccessToken()'s 30s polling loop would silently repeat the
      // exact same acquireTokenSilent → same wrong cached account → same 403 →
      // "will retry" forever, with zero user-visible recovery path (found 2026-07-27:
      // an admin-green login stuck in this exact loop after the browser's CIAM
      // session was for a different account than the one with backend access).
      // Force the real Microsoft account picker instead of spinning.
      if (err?.isAuthRejection) {
        console.warn(
          "[Auth] Backend rejected the cached identity — forcing account picker instead of silent retry"
        );
        this.entraLoginRedirect({ forceAccountPicker: true });
        return false;
      }

      if (msg === "no_accounts" || code === "no_accounts") {
        // MSAL has no cached account — try ssoSilent via hidden iframe to recover session.
        // Do NOT call localOnlyLogout() here: it purges MSAL state + navigates to "/" →
        // page reload with 0 accounts → this branch fires again → infinite 1-second loop.
        try {
          const msal2 = await getMsalInstanceAsync();
          if (msal2) {
            const ssoResp = await msal2.ssoSilent({ scopes: [apiScope] });
            return await this.exchangeEntraToken(ssoResp.accessToken);
          }
        } catch (ssoErr: any) {
          const ssoCode: string = ssoErr?.errorCode ?? "";
          console.warn(
            "[Auth] ssoSilent failed after no_accounts:",
            ssoCode || ssoErr?.message
          );
          if (
            SESSION_EXPIRED_CODES.has(ssoCode) ||
            ssoCode === "login_required"
          ) {
            // Do NOT call localOnlyLogout() — it navigates to "/" which reloads with 0 accounts
            // and retriggers this branch in a loop. Send the user to CIAM to re-authenticate.
            console.warn(
              "[Auth] Session gone — redirecting to login (not localOnlyLogout)"
            );
            this.loginRedirect();
          }
        }
        return false;
      }

      if (SESSION_EXPIRED_CODES.has(code)) {
        // Session truly expired — redirect to login so the user can re-authenticate cleanly.
        // Do NOT call localOnlyLogout(): it navigates to "/" with no MSAL state, which causes
        // checkAccessToken() to fire immediately with 0 accounts → ssoSilent → login_required
        // → localOnlyLogout() → navigate to "/" → infinite loop.
        console.warn(
          "[Auth] Entra session expired, redirecting to login:",
          code
        );
        this.loginRedirect();
      } else {
        // Transient error (network, iframe timeout, MSAL bug) — log and retry next cycle.
        // Don't wipe MSAL state or force a page reload; the current JWT may still be valid.
        console.warn(
          "[Auth] Entra silent refresh transient error (will retry):",
          code || err?.message
        );
      }
      return false;
    }
  }
}

const authService = new AuthService();

let checkAccessTokenTimeout: any;

// Public pages that must NEVER trigger auth renewal
const PUBLIC_PATHS = [
  "/auth/callback",
  "/logout",
  "/logout/callback",
  "/marketplace/activate"
];

// Public pages keyed by token in the URL (/sign/:token, /quote/:token, /q/:token) — matched
// by prefix since the exact path varies per link. Missing from here means an unauthenticated
// visitor on one of these links gets silently bounced to login by the background refresh
// below before the page can render (found 2026-08-01: /marketplace/activate had the same gap).
const PUBLIC_PATH_PREFIXES = ["/sign/", "/quote/", "/q/"];

export function isPublicPage(): boolean {
  const path = window.location.pathname;
  return (
    PUBLIC_PATHS.includes(path) ||
    PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))
  );
}

let focusDebounceTimeout: any;

function checkAccessToken() {
  // Never schedule the real 30s polling loop under Vitest. This function
  // reschedules itself via setTimeout on every branch below, forever, with
  // no exported stop/cleanup hook. Vitest's jsdom environment uses REAL
  // timers by default, so any test that transitively imports AuthService.ts
  // (most do, via App.tsx/PrivateRoute/SiteProvider/StatusProvider) starts
  // this chain at module load with nothing to ever clear it. Found while
  // investigating a 2026-07-21 CI hang (root cause there turned out to be a
  // single unrelated stuck test, see vite.config.ts's exclude list) -- kept
  // as its own fix regardless, since a real production timer with no
  // test-environment guard is a genuine bug independent of that incident.
  // Does not touch any of the three login-loop guards (focus debounce,
  // _entraRefreshInProgress check, redirectFired ref) -- see
  // docs/architecture/auth-login-loop-postmortem.md.
  if (import.meta.env.VITEST) {
    return;
  }

  if (isPublicPage()) {
    clearTimeout(checkAccessTokenTimeout);
    checkAccessTokenTimeout = setTimeout(() => checkAccessToken(), 30000);
    return;
  }

  // Don't attempt token refresh while an MSAL login redirect is in flight —
  // entraTokenRefresh() calls localOnlyLogout() when there are no accounts,
  // which wipes sessionStorage and destroys the PKCE verifier mid-redirect.
  //
  // 2026-08-17 fix: an abandoned stamp (interrupted/blocked redirect that never called
  // clearMsalLoginStamp()) used to wedge this check forever — this function returns before
  // ever calling getMsalInstance(), which is the only other place that stamp's staleness ever
  // got checked, so nothing could self-heal short of the user manually clearing storage.
  // clearMsalLoginStampIfStale() self-heals here directly: a stamp older than 5 minutes is
  // treated as abandoned, cleared, and refresh proceeds normally on this very check.
  clearMsalLoginStampIfStale();
  const loginStamp = localStorage.getItem("velaro.msalLoginStartedAt");
  if (loginStamp) {
    clearTimeout(checkAccessTokenTimeout);
    checkAccessTokenTimeout = setTimeout(() => checkAccessToken(), 30000);
    return;
  }

  // Don't fire a second concurrent refresh — PrivateRoute may already be running one.
  if ((authService as any)._entraRefreshInProgress) {
    clearTimeout(checkAccessTokenTimeout);
    checkAccessTokenTimeout = setTimeout(() => checkAccessToken(), 30000);
    return;
  }

  const expires = authService.token.expires;
  const delta = expires.getTime() - Date.now() - 1000 * 60 * 10;

  if (delta < 0) {
    console.log("token expires soon, renewing");
    authService.entraTokenRefresh();
  }

  clearTimeout(checkAccessTokenTimeout);
  checkAccessTokenTimeout = setTimeout(() => checkAccessToken(), 30000);
}

window.addEventListener("focus", () => {
  // Debounce focus events — rapid focus/blur cycles (e.g. during MSAL redirect)
  // previously fired entraTokenRefresh() concurrently with PrivateRoute's refresh,
  // causing auth state churn that remounted the entire provider tree each cycle.
  clearTimeout(focusDebounceTimeout);
  focusDebounceTimeout = setTimeout(() => checkAccessToken(), 2000);
});

// Run immediately on page load so an expired token is refreshed before the first
// API call fires. The 30-second polling loop then keeps it current.
// (No-op under Vitest, guarded inside checkAccessToken() itself, see comment there.)
checkAccessToken();

export default authService;
